import { useEffect, useMemo, useState } from 'react';
import { shell } from '../../shell/shellClasses';
import { formatInt, formatMoney } from '../daily-brief/format';
import { BlockCsvButton } from '../debug-export/BlockCsvButton';
import { indiaMapCalcCsv } from '../debug-export/blockCsv';
import { downloadCsv } from '../debug-export/csv';
import { IndiaMapCanvas } from './IndiaMapCanvas';
import { State360Panel } from './State360Panel';
import { WarehouseSummaryPanel } from './WarehouseSummaryPanel';
import {
  LIVE_MAP_LAYERS,
  WAREHOUSE_OVERLAY_LAYERS,
  type IndiaMapModel,
  type IndiaStateCode,
  type MapLayerId,
  type WarehouseOverlayId,
} from './types';
import type { WarehouseMapRow } from './warehouseLayer';

/** FOUNDER-DASHBOARD-IMPLEMENTATION-000007 — India Business Intelligence Engine */
export function IndiaBusinessMap({
  model,
  loading,
  error,
  onRetry,
}: {
  model: IndiaMapModel | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const [layer, setLayer] = useState<MapLayerId>('sales');
  const [hover, setHover] = useState<IndiaStateCode | null>(null);
  const [active, setActive] = useState<IndiaStateCode | null>(null);
  const [overlays, setOverlays] = useState<Record<WarehouseOverlayId, boolean>>({
    warehouse_locations: true,
    warehouse_coverage: false,
  });
  const [activeWh, setActiveWh] = useState<WarehouseMapRow | null>(null);

  useEffect(() => {
    setActive(null);
    setHover(null);
    setActiveWh(null);
  }, [model?.connectionId, model?.periodFrom, model?.periodTo]);

  const activeState = useMemo(() => {
    if (!model || !active) return null;
    return model.states.find((s) => s.code === active) ?? null;
  }, [model, active]);

  const stateWarehouses = useMemo(() => {
    if (!model || !active) return [];
    return model.warehouses.filter((w) => w.stateCode === active);
  }, [model, active]);

  const tip = useMemo(() => {
    if (!model) return null;
    const code = hover ?? active;
    if (!code) return null;
    return model.states.find((s) => s.code === code) ?? null;
  }, [model, hover, active]);

  const toggleOverlay = (id: WarehouseOverlayId) => {
    setOverlays((o) => ({ ...o, [id]: !o[id] }));
  };

  return (
    <section
      id="widget-india-map"
      data-widget-id="india-map"
      aria-labelledby="india-map-heading"
      className="space-y-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="india-map-heading"
          className="text-sm font-semibold tracking-tight text-[var(--shell-fg)]"
        >
          Where your sales come from
          {model ? <span className={`ml-2 font-normal ${shell.muted}`}>{model.periodLabel}</span> : null}
        </h2>
        <BlockCsvButton
          disabled={!model}
          onClick={() => {
            if (!model) return;
            downloadCsv(
              `dashboard_india-map_${model.periodFrom}_${model.periodTo}.csv`,
              indiaMapCalcCsv(model),
            );
          }}
        />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Heat layers">
          <span className={`text-[10px] font-medium uppercase tracking-wide ${shell.subtle}`}>
            Heat
          </span>
          {LIVE_MAP_LAYERS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`${shell.control} ${shell.focusRing} min-h-[44px] ${
                layer === l.id ? 'border-[var(--shell-accent)] text-[var(--shell-accent)]' : ''
              }`}
              aria-pressed={layer === l.id}
              onClick={() => setLayer(l.id)}
              disabled={Boolean(model?.heatGated)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div
          className="flex flex-wrap items-center gap-2"
          role="toolbar"
          aria-label="Warehouse layers"
        >
          <span className={`text-[10px] font-medium uppercase tracking-wide ${shell.subtle}`}>
            Warehouse
          </span>
          {WAREHOUSE_OVERLAY_LAYERS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`${shell.control} ${shell.focusRing} min-h-[44px] ${
                overlays[l.id] ? 'border-[var(--shell-accent)] text-[var(--shell-accent)]' : ''
              }`}
              aria-pressed={overlays[l.id]}
              onClick={() => toggleOverlay(l.id)}
              disabled={!model}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-[var(--shell-radius-md)] bg-[var(--shell-danger)]/10 px-3 py-2 text-sm text-[var(--shell-danger)]"
        >
          {error}{' '}
          <button type="button" className={`underline ${shell.focusRing}`} onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {!model && !loading && !error && (
        <div className={`rounded-[var(--shell-radius-md)] px-4 py-8 text-center text-sm ${shell.muted}`}>
          Connect your store to see where your sales come from.
        </div>
      )}

      {loading && !model && (
        <div
          className={`h-[360px] animate-pulse rounded-[var(--shell-radius-md)] bg-[var(--shell-context)]`}
          aria-busy="true"
          aria-label="Loading India map"
        />
      )}

      {model && (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs" role="status">
            <span className={shell.muted}>
              Sales{' '}
              <strong className="tabular-nums text-[var(--shell-fg)]">
                {formatMoney(model.totalGmvKnownGeo, model.currency)}
              </strong>
            </span>
            <span className={shell.muted}>
              Orders{' '}
              <strong className="tabular-nums text-[var(--shell-fg)]">
                {formatInt(model.totalOrdersKnownGeo)}
              </strong>
            </span>
            <span className={shell.muted}>
              Warehouses{' '}
              <strong className="tabular-nums text-[var(--shell-fg)]">
                {formatInt(model.warehouseMasterTotal)}
              </strong>
            </span>
          </div>

          {model.heatGated && (
            <div
              role="status"
              className="rounded-[var(--shell-radius-md)] bg-amber-500/10 px-3 py-2 text-sm text-[var(--shell-fg)]"
            >
              {model.gateReason}
            </div>
          )}

          {model.insights.length > 0 && (
            <ul
              className="space-y-1 text-xs text-[var(--shell-fg)]"
              aria-label="Key insights"
            >
              {model.insights.map((ins) => (
                <li key={ins.id} className="leading-snug">
                  <span className={shell.accent}>·</span> {ins.text}
                </li>
              ))}
            </ul>
          )}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.9fr)]">
          <IndiaMapCanvas
            model={model}
            layer={layer}
            activeCode={active}
            hoverCode={hover}
            onHover={setHover}
            onSelect={setActive}
            heatGated={model.heatGated}
            showWarehousePins={overlays.warehouse_locations}
            showWarehouseCoverage={overlays.warehouse_coverage}
            activeWarehouseId={activeWh?.id ?? null}
            onSelectWarehouse={(w) => {
              setActiveWh(w);
              setActive(null);
            }}
          />

          <div>
            <h3 className={`mb-2 text-xs font-medium ${shell.muted}`}>Cities</h3>
            {model.topCities.length === 0 ? (
              <p className={`text-sm ${shell.muted}`}>No city-level orders in this period.</p>
            ) : (
              <ul
                className="max-h-[360px] space-y-1 overflow-y-auto"
                aria-label="City ranking"
              >
                {model.topCities.map((c) => (
                  <li
                    key={`${c.city}|${c.state ?? ''}`}
                    className={`${shell.control} flex min-h-[44px] items-center justify-between gap-2 text-left`}
                  >
                    <span className="truncate">
                      <span className="font-medium text-[var(--shell-fg)]">{c.city}</span>
                      {c.state ? (
                        <span className={`block text-[11px] ${shell.muted}`}>{c.state}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-right tabular-nums text-xs">
                      {layer === 'sales'
                        ? formatMoney(c.gmv, model.currency)
                        : layer === 'orders'
                          ? formatInt(c.orders)
                          : formatInt(c.units ?? null)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>

          {tip && (
            <div
              className={`rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} px-3 py-2 text-xs`}
              role="status"
              aria-live="polite"
            >
              <span className="font-semibold text-[var(--shell-fg)]">{tip.name}</span>
              <span className={`mx-2 ${shell.muted}`}>·</span>
              GMV {formatMoney(tip.gmv, model.currency)}
              <span className={`mx-2 ${shell.muted}`}>·</span>
              Orders {formatInt(tip.orderCount)}
              <span className={`mx-2 ${shell.muted}`}>·</span>
              Units {formatInt(tip.units)}
              <span className={`mx-2 ${shell.muted}`}>·</span>
              Share {tip.contributionPct != null ? `${tip.contributionPct.toFixed(1)}%` : '—'}
              <span className={`mx-2 ${shell.muted}`}>·</span>
              Warehouses {model.warehouses.filter((w) => w.stateCode === tip.code).length}
              <span className={`mx-2 ${shell.muted}`}>·</span>
              <span className={shell.subtle}>Click for detail</span>
            </div>
          )}

          {activeState && (
            <State360Panel
              state={activeState}
              model={model}
              warehouses={stateWarehouses}
              onClose={() => setActive(null)}
              onSelectWarehouse={(w) => {
                setActiveWh(w);
              }}
            />
          )}

          {activeWh && (
            <WarehouseSummaryPanel warehouse={activeWh} onClose={() => setActiveWh(null)} />
          )}

          {overlays.warehouse_locations && model.warehouses.length > 0 && (
            <div>
              <h3 className={`mb-2 text-xs font-medium ${shell.muted}`}>
                Warehouses ({model.warehouseMasterTotal})
              </h3>
              <ul
                className="grid max-h-40 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3"
                aria-label="Warehouse list"
              >
                {model.warehouses.map((w) => (
                  <li key={w.id}>
                    <button
                      type="button"
                      className={`${shell.control} ${shell.focusRing} flex w-full min-h-[44px] items-center justify-between gap-2 text-left ${
                        activeWh?.id === w.id
                          ? 'border-[var(--shell-accent)] text-[var(--shell-accent)]'
                          : ''
                      }`}
                      onClick={() => setActiveWh(w)}
                    >
                      <span className="truncate">
                        <span className="font-mono text-[11px]">{w.code}</span> {w.name}
                        <span className={`block text-[10px] ${shell.muted}`}>
                          {[w.city, w.stateLabel].filter(Boolean).join(', ') || 'Location unknown'}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono text-[10px] opacity-70">
                        {w.warehouseType}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-2">
            <h3 className={`mb-2 text-xs font-medium ${shell.muted}`}>States</h3>
            {model.ranked.length === 0 ? (
              <p className={`text-sm ${shell.muted}`}>
                No state-level orders in this period.
              </p>
            ) : (
              <ul
                className="grid max-h-48 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3"
                aria-label="State ranking"
              >
                {model.ranked.map((code) => {
                  const s = model.states.find((x) => x.code === code)!;
                  return (
                    <li key={code}>
                      <button
                        type="button"
                        className={`${shell.control} ${shell.focusRing} flex w-full min-h-[44px] items-center justify-between gap-2 text-left ${
                          active === code
                            ? 'border-[var(--shell-accent)] text-[var(--shell-accent)]'
                            : ''
                        }`}
                        onClick={() => setActive(code)}
                        onMouseEnter={() => setHover(code)}
                        onMouseLeave={() => setHover(null)}
                      >
                        <span className="truncate">
                          <span className="font-mono text-[11px] opacity-70">
                            {s.nationalRank != null ? `#${s.nationalRank}` : code}
                          </span>{' '}
                          {s.name}
                        </span>
                        <span className="shrink-0 tabular-nums text-xs">
                          {layer === 'sales'
                            ? formatMoney(s.gmv, model.currency)
                            : layer === 'orders'
                              ? formatInt(s.orderCount)
                              : formatInt(s.units)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
